import { Validators } from "@angular/forms";
import { merge, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { PayeeCaseSummaryControls, SummaryControls, SummaryForm } from "../form.controls";
import { COFACE_FORM_NAME, FormFactory, NOTICE_FORM_NAME, SUMMARY_FORM_NAME } from "../form.factory";
import { ProductType, Summary } from "../form.resources";

export function getSummaryForm(this: FormFactory): SummaryForm {
  const data = this.data as Summary;

  const form = this.fb.group<SummaryControls>({
    product_type: this.fb.control(data.product.type, { nonNullable: true }),
    payee_case: this.fb.group<PayeeCaseSummaryControls>({
      is_start_case_price_claim_added: this.fb.control({ value: data.payee_case.is_start_case_price_claim_added, disabled: true }, { nonNullable: true }),
      is_interest_added: this.fb.control(data.payee_case.is_interest_added, { nonNullable: true }),
      is_flat_rate_cost_claim_added: this.fb.control(data.payee_case.is_flat_rate_cost_claim_added, { nonNullable: true }),
    }),
  });

  const defaultForms = data.product.forms[data.product.type];

  if (defaultForms.includes(COFACE_FORM_NAME)) {
    form.setControl(COFACE_FORM_NAME, this.getCofaceForm());
  }

  if (defaultForms.includes('debtor_form')) {
    const noticeForm = this.getNoticeForm();
    form.setControl(NOTICE_FORM_NAME, noticeForm);

    const debtorEmail = noticeForm.controls.email;
    debtorEmail.setValidators(defaultForms.includes('debtor_form')
      ? [Validators.required, Validators.email]
      : []);
    debtorEmail.updateValueAndValidity();
  }

  if (!data.product.is_product_type_editable) {
    form.controls.product_type.disable({ onlySelf: true });
  }

  form.controls.payee_case.controls.is_flat_rate_cost_claim_added.setValidators(defaultForms.includes('flat_rate_cost_added_form')
    ? [Validators.required]
    : []);
  form.controls.payee_case.controls.is_flat_rate_cost_claim_added.updateValueAndValidity();
  form.controls.payee_case.controls.is_interest_added.setValidators(defaultForms.includes('interest_added_form')
    ? [Validators.required]
    : []);
  form.controls.payee_case.controls.is_interest_added.updateValueAndValidity();

  this.formService.saving
    .pipe(distinctUntilChanged(), debounceTime(0))
    .subscribe({
      next: saving => {
        if (saving > 0) {
          form.disable({ emitEvent: false });
        } else {
          form.enable({ emitEvent: false });
        }
      },
    });

  merge(
    form.controls.payee_case.controls.is_flat_rate_cost_claim_added.valueChanges.pipe(map(v => ({ is_flat_rate_cost_claim_added: v }))),
    form.controls.payee_case.controls.is_interest_added.valueChanges.pipe(map(v => ({ is_interest_added: v }))),
  )
    .subscribe({
      next: async (v) => {
        try {
          const value = form.getRawValue();
          await this.formService.selectOptionalClaims({
            is_flat_rate_cost_claim_added: value.payee_case.is_flat_rate_cost_claim_added,
            is_interest_added: value.payee_case.is_interest_added,
            ...v,
          });
        } catch (error) {
          console.error('Error while saving optional claims', error);
        }
      },
    });

  if (data.product.is_product_type_editable) {
    form.controls.product_type.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe({
        next: async (product_type: ProductType) => {
          try {
            await this.formService.changeProduct(product_type);
          } catch (error) {
            console.error('Error while changing product', error);
          }

          const forms = data.product.forms[product_type];
          const debtorEmail = form.controls[NOTICE_FORM_NAME]?.controls.email;
          debtorEmail?.setValidators(forms.includes('debtor_form')
            ? [Validators.required]
            : []);
          debtorEmail?.updateValueAndValidity({ emitEvent: false });
          form.controls.payee_case.controls.is_flat_rate_cost_claim_added.setValidators(forms.includes('flat_rate_cost_added_form')
            ? [Validators.required]
            : []);
          form.controls.payee_case.controls.is_flat_rate_cost_claim_added.updateValueAndValidity({ emitEvent: false });
          form.controls.payee_case.controls.is_interest_added.setValidators(forms.includes('interest_added_form')
            ? [Validators.required]
            : []);
          form.controls.payee_case.controls.is_interest_added.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  form.statusChanges.subscribe(status => {
    if (status === 'VALID') {
      this.trackValidForm(SUMMARY_FORM_NAME);
    }
  });

  return form;
}
